/**
 * Tracing filters for Sentry's browserTracingIntegration and beforeSend hooks.
 * See docs/code/lib.md for the GlitchTip #3064 and #3365 incident narratives
 * behind these rules.
 */

/**
 * Decide whether browserTracingIntegration should create a span for an
 * outgoing request. Used as `shouldCreateSpanForRequest`.
 *
 * Returns false for the health connectivity probe (any origin, since prod
 * serves the API from a separate host), true for everything else.
 */
export function shouldCreateSpanForRequest(url: string): boolean {
  return !isHealthProbeUrl(url);
}

/** Matches the connectivity probe endpoint `.../health/` regardless of origin. */
export function isHealthProbeUrl(url: string): boolean {
  return /\/health\/?(?:\?.*)?$/.test(url);
}

/**
 * Returns true when the Sentry exception type is a Workbox no-response error.
 *
 * WorkboxError sets `error.name = 'no-response'`, which Sentry maps to
 * `exception.values[0].type`. These rejections are always network-level
 * failures — the page fetch code already handles them; capturing them in the
 * service worker adds noise with no diagnostic value (see GlitchTip #3365).
 */
export function isWorkboxNoResponseError(exceptionType: string): boolean {
  return exceptionType === 'no-response';
}

/**
 * Returns true when the Sentry exception values indicate an AbortError.
 *
 * AbortErrors (DOMException code 20) are expected whenever a fetch is
 * cancelled — user navigation, component unmount, React Query cleanup, or the
 * browser stopping a request (Safari in particular fires these as unhandled
 * rejections). They are not bugs and must not reach GlitchTip.
 *
 * GlitchTip #1721 added this filter for the main-thread Sentry init.
 * GlitchTip #3428: the same error was slipping through the SW's beforeSend,
 * which only checked for Workbox errors. The exc may appear as
 * `{ type: 'AbortError' }` or `{ type: 'Error', value: 'AbortError: AbortError' }`
 * depending on how Safari serialises the thrown DOMException.
 */
export function isAbortError(
  exc: { type?: string; value?: string } | undefined,
): boolean {
  if (!exc) return false;
  return exc.type === 'AbortError' || (exc.value?.includes('AbortError') ?? false);
}

type SentryExceptionValue = { type?: string; value?: string } | undefined;

/** True when the serialised exception or the original thrown value is the named DOMException. */
function isNamedBrowserError(exc: SentryExceptionValue, original: unknown, name: string): boolean {
  if (exc?.type === name || (exc?.value?.includes(name) ?? false)) return true;
  return (original instanceof Error || original instanceof DOMException) && original.name === name;
}

/**
 * Returns true for a browser-level failure that is not an application bug: an
 * AbortError (a cancelled fetch) or a NetworkError (DOMException code 19, a fetch
 * that failed at the network layer). Checks both the serialised exception and the
 * original thrown value, since Safari wraps the DOMException in an Error.
 *
 * The page's `beforeSend` decision. GlitchTip #3428 (AbortError) and #3433
 * (NetworkError) were each filtered on the page first and kept firing from the
 * service worker, whose filter was a separate copy: see below.
 */
export function isTransientBrowserError(exc: SentryExceptionValue, original: unknown): boolean {
  return isNamedBrowserError(exc, original, 'AbortError') || isNamedBrowserError(exc, original, 'NetworkError');
}

/**
 * The service worker's `beforeSend` decision. Drops Workbox no-response and
 * AbortError as before, and a NetworkError only when nothing in `sw.ts` reported
 * it on purpose: a `captureException` there carries `extra.where`, and a
 * NetworkError from the Cache API on that path is a real failure (GlitchTip
 * #5377), unlike a fetch that failed at the network layer.
 */
export function shouldDropServiceWorkerEvent(
  exc: SentryExceptionValue,
  original: unknown,
  reportedWhere?: unknown,
): boolean {
  if (isWorkboxNoResponseError(exc?.type ?? '') || isNamedBrowserError(exc, original, 'AbortError')) return true;
  return reportedWhere == null && isNamedBrowserError(exc, original, 'NetworkError');
}
